/**
 * 来場者ホーム集約データのTTLメモ化・同時実行重複排除ユーティリティ
 * ホームフォーカスの都度DB往復が発生する問題を軽減するため、
 * 短時間の再取得は最後に取得した結果を再利用し、同時に走る取得は1回のPromiseへ集約する
 */

/** ホーム集約データのキャッシュ有効期間（ミリ秒） */
export const VISITOR_HOME_BOOTSTRAP_CACHE_TTL_MS = 20_000;

/** キャッシュエントリ */
type CacheEntry<T> = {
  /** キャッシュされた値 */
  value: T;
  /** 取得完了時刻（epoch ms） */
  fetchedAt: number;
};

/** セッショントークンごとのキャッシュ済み値 */
const cacheEntriesBySessionToken = new Map<string, CacheEntry<unknown>>();

/** 進行中の取得 */
type InFlightEntry = {
  /** 取得処理のPromise */
  promise: Promise<unknown>;
  /** 取得開始時の世代番号 */
  generation: number;
};

/** セッショントークンごとの進行中の取得（in-flight dedupe） */
const inFlightEntriesBySessionToken = new Map<string, InFlightEntry>();

/**
 * 取得の開始と破棄のたびに増やす通し番号。
 * 取得は開始時の番号を持ち、終わった時点でその番号がまだ最新の場合だけ結果をキャッシュする
 */
let generationCounter = 0;

/** セッショントークンごとの最新の世代番号（最後に始めた取得、または最後の破棄） */
const latestGenerationsBySessionToken = new Map<string, number>();

/** 全セッションを破棄した時点の世代番号。これ以前に始めた取得はキャッシュしない */
let clearedAllAtGeneration = 0;

/**
 * セッショントークンの世代を1つ進め、新しい世代番号を返す
 * @param {string} sessionToken - 来場者セッショントークン
 * @returns {number} 新しい世代番号
 */
const advanceGeneration = (sessionToken: string): number => {
  generationCounter += 1;
  latestGenerationsBySessionToken.set(sessionToken, generationCounter);
  return generationCounter;
};

/**
 * 取得開始時の世代番号が、まだそのセッショントークンの最新か判定する
 * 取得中に破棄された、または後から別の取得（forceRefresh）が始まった場合はfalse
 * @param {string} sessionToken - 来場者セッショントークン
 * @param {number} generation - 取得開始時の世代番号
 * @returns {boolean} 最新ならtrue
 */
const isLatestGeneration = (sessionToken: string, generation: number): boolean => (
  generation > clearedAllAtGeneration
  && latestGenerationsBySessionToken.get(sessionToken) === generation
);

/**
 * セッショントークンに対応するキャッシュ済み値が有効期間内か判定する
 * @param {string} sessionToken - 来場者セッショントークン
 * @param {number} ttlMs - 有効期間（ミリ秒）
 * @param {number} nowMs - 現在時刻（epoch ms）
 * @returns {boolean} 有効期間内ならtrue
 */
const isCacheEntryFresh = (sessionToken: string, ttlMs: number, nowMs: number): boolean => {
  const cacheEntry = cacheEntriesBySessionToken.get(sessionToken);

  if (!cacheEntry) {
    return false;
  }

  return nowMs - cacheEntry.fetchedAt < ttlMs;
};

/**
 * TTLメモ化・in-flight dedupeを適用して取得処理を実行する
 * @template T
 * @param {string} sessionToken - 来場者セッショントークン（キャッシュキー）
 * @param {() => Promise<T>} fetcher - 実際の取得処理
 * @param {{ ttlMs?: number, forceRefresh?: boolean }} [options={}] - オプション
 * @returns {Promise<T>} 取得結果（キャッシュ・進行中Promiseを優先利用する）
 */
export const withVisitorHomeBootstrapCache = async <T>(
  sessionToken: string,
  fetcher: () => Promise<T>,
  options: { ttlMs?: number; forceRefresh?: boolean } = {},
): Promise<T> => {
  const ttlMs = options.ttlMs ?? VISITOR_HOME_BOOTSTRAP_CACHE_TTL_MS;
  const forceRefresh = options.forceRefresh ?? false;
  const nowMs = Date.now();

  if (!forceRefresh && isCacheEntryFresh(sessionToken, ttlMs, nowMs)) {
    return cacheEntriesBySessionToken.get(sessionToken)!.value as T;
  }

  /** 同一セッショントークンで進行中の取得があれば、それに相乗りする */
  const existingInFlightPromise = !forceRefresh
    ? (inFlightEntriesBySessionToken.get(sessionToken)?.promise as Promise<T> | undefined)
    : undefined;

  if (existingInFlightPromise) {
    return existingInFlightPromise;
  }

  /** 今回の取得の世代番号 */
  const generation = advanceGeneration(sessionToken);

  /** 今回の取得処理Promise */
  const fetchPromise: Promise<T> = (async () => {
    try {
      const fetchedValue = await fetcher();
      // 取得中に破棄・追い越しがあった場合、古い値を有効期間付きで戻さない
      if (isLatestGeneration(sessionToken, generation)) {
        cacheEntriesBySessionToken.set(sessionToken, {
          value: fetchedValue,
          fetchedAt: Date.now(),
        });
      }
      return fetchedValue;
    } finally {
      // 後から始まった取得の相乗り先は消さない
      if (inFlightEntriesBySessionToken.get(sessionToken)?.generation === generation) {
        inFlightEntriesBySessionToken.delete(sessionToken);
      }
    }
  })();

  inFlightEntriesBySessionToken.set(sessionToken, { promise: fetchPromise, generation });

  return fetchPromise;
};

/**
 * ホーム集約データのキャッシュを破棄する
 * pt取得・交換等の残高が変わる操作の直後に呼び、古いptを表示し続けないようにする。
 * 破棄の前に始まっていた取得の結果もキャッシュしない
 * @param {string} [sessionToken] - 破棄対象のセッショントークン（省略時は全セッション分を破棄）
 * @returns {void}
 */
export const invalidateVisitorHomeBootstrapCache = (sessionToken?: string): void => {
  if (sessionToken) {
    advanceGeneration(sessionToken);
    cacheEntriesBySessionToken.delete(sessionToken);
    inFlightEntriesBySessionToken.delete(sessionToken);
    return;
  }

  generationCounter += 1;
  clearedAllAtGeneration = generationCounter;
  latestGenerationsBySessionToken.clear();
  cacheEntriesBySessionToken.clear();
  inFlightEntriesBySessionToken.clear();
};
